import {
  TEntityResponse,
  TEntityListResponse,
  TDeviceProfileRevisionResBody,
  TDeviceProfileRevisionSummaryResBody,
  TDeviceSummaryResBody,
  TDeviceProfileActionSummaryResBody,
  TDeviceProfileErrorSummaryResBody,
  TDeviceProfileEventSummaryResBody,
  TDeviceProfileMetricSummaryResBody,
  TDeviceProfileStateSummaryResBody,
  TDeviceProfileConfigSummaryResBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TDeviceProfileRevisionsClient = {
  get(id: string): Promise<TEntityResponse<'device-profile-revision', TDeviceProfileRevisionResBody> | undefined>;
  list(
    limit: number,
    page: number,
    deviceTypeId?: string
  ): Promise<TEntityListResponse<'device-profile-revision', TDeviceProfileRevisionSummaryResBody> | undefined>;
  list_dpr_devices(id: string): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined>;
  list_dpr_actions(
    id: string
  ): Promise<TEntityListResponse<'device-profile-action', TDeviceProfileActionSummaryResBody> | undefined>;
  list_dpr_errors(
    id: string
  ): Promise<TEntityListResponse<'device-profile-error', TDeviceProfileErrorSummaryResBody> | undefined>;
  list_dpr_events(
    id: string
  ): Promise<TEntityListResponse<'device-profile-event', TDeviceProfileEventSummaryResBody> | undefined>;
  list_dpr_configs(
    id: string
  ): Promise<TEntityListResponse<'device-profile-config', TDeviceProfileConfigSummaryResBody> | undefined>;
  list_dpr_metrics(
    id: string
  ): Promise<TEntityListResponse<'device-profile-metric', TDeviceProfileMetricSummaryResBody> | undefined>;
  list_dpr_states(
    id: string
  ): Promise<TEntityListResponse<'device-profile-state', TDeviceProfileStateSummaryResBody> | undefined>;
};

function DeviceProfileRevisionsClient(apiClient: TAPIClient): TDeviceProfileRevisionsClient {
  return {
    async get(
      id: string
    ): Promise<TEntityResponse<'device-profile-revision', TDeviceProfileRevisionResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'device-profile-revision', TDeviceProfileRevisionResBody>>(
        `/device-profile-revisions/${id}`
      );

      if (data && data.type !== 'device-profile-revision') {
        throw new Error('Invalid entity type received, expected: device-profile-revision, received: ' + data.type);
      }

      return data;
    },

    async list(
      limit: number,
      page: number,
      deviceTypeId?: string
    ): Promise<TEntityListResponse<'device-profile-revision', TDeviceProfileRevisionSummaryResBody> | undefined> {
      let url = `/device-profile-revisions?limit=${limit}&page=${page}`;

      if (deviceTypeId) {
        url += `&filter[device_type_id]=${deviceTypeId}`;
      }

      const data =
        await apiClient.get<TEntityListResponse<'device-profile-revision', TDeviceProfileRevisionSummaryResBody>>(url);

      if (data && data.type !== 'device-profile-revision') {
        throw new Error('Invalid entity type received, expected: device-profile-revision, received: ' + data.type);
      }

      return data;
    },

    async list_dpr_actions(
      id: string
    ): Promise<TEntityListResponse<'device-profile-action', TDeviceProfileActionSummaryResBody> | undefined> {
      const data = await apiClient.get<
        TEntityListResponse<'device-profile-action', TDeviceProfileActionSummaryResBody>
      >(`/device-profile-revisions/${id}/actions`);

      if (data && data.type !== 'device-profile-action') {
        throw new Error('Invalid entity type received, expected: device-profile-action, received: ' + data.type);
      }

      return data;
    },

    async list_dpr_configs(
      id: string
    ): Promise<TEntityListResponse<'device-profile-config', TDeviceProfileConfigSummaryResBody> | undefined> {
      const data = await apiClient.get<
        TEntityListResponse<'device-profile-config', TDeviceProfileConfigSummaryResBody>
      >(`/device-profile-revisions/${id}/configs`);

      if (data && data.type !== 'device-profile-config') {
        throw new Error('Invalid entity type received, expected: device-profile-config, received: ' + data.type);
      }

      return data;
    },

    async list_dpr_devices(id: string): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
        `/device-profile-revisions/${id}/devices`
      );

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async list_dpr_errors(
      id: string
    ): Promise<TEntityListResponse<'device-profile-error', TDeviceProfileErrorSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-profile-error', TDeviceProfileErrorSummaryResBody>>(
        `/device-profile-revisions/${id}/errors`
      );

      if (data && data.type !== 'device-profile-error') {
        throw new Error('Invalid entity type received, expected: device-profile-error, received: ' + data.type);
      }

      return data;
    },

    async list_dpr_events(
      id: string
    ): Promise<TEntityListResponse<'device-profile-event', TDeviceProfileEventSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-profile-event', TDeviceProfileEventSummaryResBody>>(
        `/device-profile-revisions/${id}/events`
      );

      if (data && data.type !== 'device-profile-event') {
        throw new Error('Invalid entity type received, expected: device-profile-event, received: ' + data.type);
      }

      return data;
    },

    async list_dpr_metrics(
      id: string
    ): Promise<TEntityListResponse<'device-profile-metric', TDeviceProfileMetricSummaryResBody> | undefined> {
      const data = await apiClient.get<
        TEntityListResponse<'device-profile-metric', TDeviceProfileMetricSummaryResBody>
      >(`/device-profile-revisions/${id}/metrics`);

      if (data && data.type !== 'device-profile-metric') {
        throw new Error('Invalid entity type received, expected: device-profile-metric, received: ' + data.type);
      }

      return data;
    },

    async list_dpr_states(
      id: string
    ): Promise<TEntityListResponse<'device-profile-state', TDeviceProfileStateSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-profile-state', TDeviceProfileStateSummaryResBody>>(
        `/device-profile-revisions/${id}/states`
      );

      if (data && data.type !== 'device-profile-state') {
        throw new Error('Invalid entity type received, expected: device-profile-state, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TDeviceProfileRevisionsClient, DeviceProfileRevisionsClient };
